import React, { createContext, useEffect, useState } from "react";

import { Header } from "~/components/layout/Header";
import { Navbar } from "~/components/layout/Navbar";
import { AppLoader } from "~/components/loading";
import { useActionCenter } from "~/contexts/actionCenterProvider";
import { env } from "~/env";
import { PortfolioViewByFields } from "~/modules/portfolio/rollups";
import { PriceBookViewByFields } from "~/modules/price-book/rollups";
import {
  Alert,
  Anchor,
  APPLICATION_FRAME_ACTION_CENTER_WIDTH,
  ApplicationFrame,
  ApplicationFrameContent,
  Flex,
  Text,
  tokens,
  useMediaQuery,
  useSessionStorage,
} from "~/ui";
import * as api from "~/utils/api";

import { ActionCenter } from "./action-center";
import { GlobalAlert } from "./layout/GlobalAlert";

export interface AppContainerProps {
  children: React.ReactNode;
}

export const MenuContext = createContext<{
  navbarOpened?: boolean;
  setNavbarOpened?: React.Dispatch<React.SetStateAction<boolean>>;
  asideOpened?: boolean;
  setAsideOpened?: React.Dispatch<React.SetStateAction<boolean>>;
  portfolioViewBy?: PortfolioViewByFields[];
  setPortfolioViewBy?: React.Dispatch<React.SetStateAction<PortfolioViewByFields[]>>;
  priceBookViewBy?: PriceBookViewByFields[];
  setPriceBookViewBy?: React.Dispatch<React.SetStateAction<PriceBookViewByFields[]>>;
}>({ navbarOpened: true });

const AppContainer = ({ children }: AppContainerProps) => {
  const actionCenterContext = useActionCenter();

  const isDesktopOrLaptop = useMediaQuery(`(min-width: ${tokens.breakpoint.md})`);
  const [navbarOpened, setNavbarOpened] = useState(isDesktopOrLaptop);
  const [asideOpened, setAsideOpened] = useState(isDesktopOrLaptop);
  const [portfolioViewBy, setPortfolioViewBy] = useSessionStorage<PortfolioViewByFields[]>({
    key: "portfolioViewBy",
    defaultValue: [PortfolioViewByFields.CUSTOMER, PortfolioViewByFields.PRODUCT, PortfolioViewByFields.PRODUCT_CODE],
  });
  const [priceBookViewBy, setPriceBookViewBy] = useSessionStorage<PriceBookViewByFields[]>({
    key: "priceBookViewBy",
    defaultValue: [PriceBookViewByFields.CUSTOMER],
  });

  const appInfo = api.api.public.appInfo.useQuery(undefined, { refetchInterval: 5 * 60 * 1000 });
  const updateNeeded = appInfo.data?.gitCommitSha && appInfo.data?.gitCommitSha !== env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA;

  useEffect(() => {
    setNavbarOpened(isDesktopOrLaptop);
    setAsideOpened(isDesktopOrLaptop);
  }, [isDesktopOrLaptop, setNavbarOpened, setAsideOpened]);

  return (
    <MenuContext.Provider
      value={{
        navbarOpened,
        setNavbarOpened,
        asideOpened,
        setAsideOpened,
        portfolioViewBy,
        setPortfolioViewBy,
        priceBookViewBy,
        setPriceBookViewBy,
      }}
    >
      <div className="app-shell--wrapper">
        <div className="app-shell--header-notifications">
          <GlobalAlert />
        </div>
        <Flex
          style={{
            flex: 1,
            maxWidth: "100vw",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Flex style={{ flex: 1 }}>
            <ApplicationFrame
              header={<Header isDesktop={isDesktopOrLaptop} navbarOpened={navbarOpened} setNavbarOpened={setNavbarOpened} />}
              isActionCenterOpen={actionCenterContext.state.isOpen}
              navigation={navbarOpened ? <Navbar isDesktop={isDesktopOrLaptop} onClose={() => setNavbarOpened(false)} /> : undefined}
            >
              <ApplicationFrameContent
                banner={
                  updateNeeded && (
                    <Alert
                      title={
                        <Text>
                          A newer version of ShelfCycle is available. <Anchor onClick={() => window.location.reload()}>Refresh</Anchor> your
                          browser to update.
                        </Text>
                      }
                    >
                      <></>
                    </Alert>
                  )
                }
                loadingIndicator={<AppLoader />}
              >
                {children}
              </ApplicationFrameContent>
            </ApplicationFrame>
          </Flex>
          <Flex
            bg={tokens.color.white}
            h={{ base: "100vh", md: "initial" }}
            maw={{ base: "100vw", md: APPLICATION_FRAME_ACTION_CENTER_WIDTH }}
            pos={{ base: "fixed", md: "initial" }}
            right={0}
            style={{ display: actionCenterContext.state.isOpen ? "flex" : "none", zIndex: 2 }}
            top={0}
            w={{ base: "100vw", md: APPLICATION_FRAME_ACTION_CENTER_WIDTH }}
          >
            <ActionCenter />
          </Flex>
        </Flex>
      </div>
    </MenuContext.Provider>
  );
};

export default AppContainer;
